/* import __COLOCATED_TEMPLATE__ from './scroll-monitor.hbs.js'; */
var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import Component from '@glimmer/component';
import { debounce, throttle, schedule, run } from '@ember/runloop';
import { getScrollPosition, getCanUseIntersectionObserver } from '@dashboard/shared-ui/utils/scroll-monitor-utils';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';

/**
## Scroll Monitor

Simple scroll monitor component that keep track of child component/element positions
relative to viewport. Primarily developed for deferring load of autoplaying video but
flexible enough to extend to lazy loading images, scroll triggered animations and more.

**NOTE** Primarily method of detecting element position is implemented using the
Intersection Observer API. When not supported (Safari) a fallback method based on
throttled scroll events is used.


```example
@title Basic Usage

@hbs
{{#scroll-monitor as |scrollMonitor|}}
  {{#scrollMonitor.section as |scrollSection|}}
    {{if scrollSection.inViewport}}
      <div>Element to render when in viewport</div>
    {{/if}}
  {{/scrollMonitor.section}}

  {{#scrollMonitor.section as |scrollSection|}}
    {{viewport-aware-component inViewport=scrollSection.inViewport}}
  {{/scrollMonitor.section}}
{{/scroll-monitor}}
```

```example
@title Custom threshold

@hbs
{{#scroll-monitor threshold=0.1 as |scrollMonitor|}}
  {{#scrollMonitor.section as |scrollSection|}}
    {{viewport-aware-component inViewport=scrollSection.inViewport}}
  {{/scrollMonitor.section}}
{{/scroll-monitor}}
```
### Parameters
@param {Number} [threshold=0.5] Ratio when element should be considered in viewport (optional)
@param {Number} [viewportHeight=window.innerHeight] Set static viewport height, primarily for testing purposes (optional)
*/

// clamps a number between a minimum and maximum value
function clamp(v, min, max) {
  return min > v ? min : max < v ? max : v;
}
let ScrollMonitor = (_class = class ScrollMonitor extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "elementsToObserve", _descriptor, this);
    _initializerDefineProperty(this, "elementsInViewport", _descriptor2, this);
    _initializerDefineProperty(this, "intersectionObserver", _descriptor3, this);
    // used for scroll fallback calculation
    _initializerDefineProperty(this, "cachedViewportHeight", _descriptor4, this);
    // optional override for static viewport height
    _initializerDefineProperty(this, "viewportHeight", _descriptor5, this);
  }
  // defaults to 0.5
  get threshold() {
    return this.args.threshold ?? 0.5;
  }
  onDidInsert() {
    const canUseIntersectionObserver = getCanUseIntersectionObserver();
    if (canUseIntersectionObserver) {
      this.initIntersectionObserver();
    } else {
      this.initScrollFallback();
    }
  }
  onWillDestroy() {
    // disconnect interesection observer if set
    this.intersectionObserver && this.intersectionObserver.disconnect();

    // remove window scroll event listener if set
    this.windowScrollListener && window.removeEventListener('scroll', this.windowScrollListener);

    // remove window resize event listener if set
    this.windowResizeListener && window.removeEventListener('resize', this.windowResizeListener);
  }
  initIntersectionObserver() {
    // bind intersection observer callback to this context
    this.handleIntersectBound = this.handleIntersect.bind(this);

    // create intersection observer with threshold based on component configuration
    // uses the document's viewport as the root, with no margin
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: this.threshold
    };
    this.intersectionObserver = new IntersectionObserver(this.handleIntersectBound, options);
  }
  handleIntersect(entries) {
    run(() => {
      // find and update elements in viewport
      entries.forEach(entry => {
        if (this.isIntersecting(entry.intersectionRatio)) {
          const id = entry.target.getAttribute('id');
          const element = this.elementsToObserve[id];
          this.updateElement(element);
        }
      });
    });
  }
  initScrollFallback() {
    // throttle window scroll to only trigger once per interval (leading edge included)
    this.windowScrollListener = () => throttle(this, this.handleScroll, 500);
    window.addEventListener('scroll', this.windowScrollListener);

    // debounce window resize to trigger once on the trailing edge of interval
    this.windowResizeListener = () => debounce(this, this.handleResize, 200);
    window.addEventListener('resize', this.windowResizeListener);

    // set initial heights and update elements after render
    schedule('afterRender', this, function () {
      this.cacheViewportAndElementHeights();
      this.calculateElementPositonAndUpdate();
    });
  }
  handleScroll() {
    run(() => {
      this.calculateElementPositonAndUpdate();
    });
  }
  handleResize() {
    run(() => {
      this.cacheViewportAndElementHeights();
    });
  }
  calculateElementPositonAndUpdate() {
    const cy = getScrollPosition();
    const ch = this.cachedViewportHeight;

    // find and update elements in viewport
    for (const id in this.elementsToObserve) {
      const element = this.elementsToObserve[id];
      const target = element.target;
      const y = target.offsetTop;
      const h = element.height;

      // calculate intersection ratio
      const intersectionRatio = (clamp(y + h, cy, cy + ch) - clamp(y, cy, cy + ch)) / h;

      // update element if intersecting based on configured threshold,
      if (this.isIntersecting(intersectionRatio)) {
        this.updateElement(element);
      }
    }
  }
  cacheViewportAndElementHeights() {
    // cache viewport height, use static value if present, otherwise use window height
    const cachedViewportHeight = this.viewportHeight || window.innerHeight;
    this.cachedViewportHeight = cachedViewportHeight;

    // cache element target heights
    for (const id in this.elementsToObserve) {
      this.elementsToObserve[id].height = this.elementsToObserve[id].target.clientHeight;
    }
  }
  registerElement(id, target) {
    // create element object,
    // cache height for usage in scroll event fallback
    const observableElement = {
      id,
      target,
      height: target.clientHeight
    };

    // add element to lookup map
    this.elementsToObserve[id] = observableElement;
    schedule('afterRender', this, function () {
      this.intersectionObserver && this.intersectionObserver.observe(observableElement.target);
    });
  }
  unregisterElement(id) {
    if (this.elementsToObserve[id]) {
      // unobserve target if using the intersection observer API
      this.intersectionObserver && this.intersectionObserver.unobserve(this.elementsToObserve[id].target);

      // remove element from lookup to exclude it from any further processing
      delete this.elementsToObserve[id];
    }
  }
  updateElement(element) {
    if (element) {
      // add element to elements in viewport state
      const nextElementsInViewport = {
        ...this.elementsInViewport,
        [element.id]: true
      };
      this.elementsInViewport = nextElementsInViewport;

      // unregister element as it is in viewport already
      this.unregisterElement(element.id);
    }
  }
  isIntersecting(intersectionRatio) {
    return intersectionRatio >= this.threshold;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "elementsToObserve", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return {};
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "elementsInViewport", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return {};
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "intersectionObserver", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "cachedViewportHeight", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "viewportHeight", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "onDidInsert", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onDidInsert"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onWillDestroy", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onWillDestroy"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "registerElement", [action], Object.getOwnPropertyDescriptor(_class.prototype, "registerElement"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "unregisterElement", [action], Object.getOwnPropertyDescriptor(_class.prototype, "unregisterElement"), _class.prototype), _class);
export { ScrollMonitor as default };